import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useState, useContext, useCallback, useEffect, useRef } from 'react';
import { GlobalContext } from 'providers/Global';
import { getKeystore, getKeystoreMeta } from '../../utils/networkConfigStore';
import { decryptFromKeystore, BadKeyError } from '../../signing/KeyManager';
import type { SigningRequestDetails } from '../../providers/SigningConfirmation';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

interface Props {
  details: SigningRequestDetails;
  onConfirm: () => void;
  onCancel: () => void;
}

const SigningConfirmationContent = ({ details, onConfirm, onCancel }: Props) => {
  const {
    connection: { accountId },
    connectedNetwork,
  } = useContext(GlobalContext);

  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Autofocus password input after render
  useEffect(() => {
    const timer = setTimeout(() => passwordRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, []);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isVerifying) onCancel();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isVerifying, onCancel]);

  const handleSign = useCallback(async () => {
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    if (!accountId || !connectedNetwork) {
      setError('Not connected');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const keystoreBase64 = getKeystore(connectedNetwork, accountId);
      if (!keystoreBase64) {
        setError('Keystore not found — please re-import your key');
        setIsVerifying(false);
        return;
      }

      const meta = getKeystoreMeta(connectedNetwork, accountId);
      await decryptFromKeystore(keystoreBase64, password, meta?.keyType);
      await new Promise((r) => setTimeout(r, 400));
      onConfirm();
    } catch (err) {
      if (err instanceof BadKeyError) {
        setError('Incorrect password');
      } else {
        setError('Verification failed');
      }
      setIsVerifying(false);
    }
  }, [password, accountId, connectedNetwork, onConfirm]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isVerifying) {
      handleSign();
    }
  };

  if (isVerifying) {
    return (
      <Box py={8} px={4}>
        <VStack spacing={3}>
          <Box
            w={10}
            h={10}
            border="3px solid"
            borderColor="green.400"
            borderTopColor="transparent"
            borderRadius="full"
            animation={`${spin} 0.8s linear infinite`}
          />
          <Text
            fontWeight={600}
            fontSize="sm"
            animation={`${pulse} 1.5s ease-in-out infinite`}
          >
            Verifying & Signing...
          </Text>
          <Text fontSize="xs" color="gray.500">
            Decrypting your key to authorize this transaction
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box px={4} py={3}>
      <VStack spacing={3} align="stretch">
        <Text fontWeight={700} fontSize="sm">
          Confirm Transaction
        </Text>

        {/* Transaction summary */}
        <Box bg="gray.50" borderRadius={8} p={3} border="1px solid" borderColor="gray.100">
          <Text fontSize="xs" color="gray.500" fontWeight={600} textTransform="uppercase" mb={1.5}>
            {details.type}
          </Text>
          <VStack spacing={1} align="stretch">
            {details.items.map((item, i) => (
              <Flex key={i} justifyContent="space-between" alignItems="baseline">
                <Text fontSize="xs" color="gray.500">{item.label}</Text>
                <Text fontSize="xs" fontWeight={500} maxW="160px" textAlign="right" wordBreak="break-all">
                  {item.value}
                </Text>
              </Flex>
            ))}
          </VStack>
        </Box>

        {/* Signing account */}
        <Flex bg="green.50" borderRadius={6} px={3} py={1.5} alignItems="center" gap={2}>
          <Box w={1.5} h={1.5} borderRadius="full" bg="green.400" />
          <Text fontSize="xs" color="green.700">
            Signing as <strong>{accountId}</strong>
          </Text>
        </Flex>

        {/* Password */}
        <FormControl isRequired>
          <FormLabel fontSize="xs" mb={1}>Enter password to sign</FormLabel>
          <Input
            ref={passwordRef}
            type="password"
            placeholder="Your keystore password"
            size="sm"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            onKeyDown={handleKeyDown}
            borderRadius={6}
          />
        </FormControl>

        {error && <Text fontSize="xs" color="red.500">{error}</Text>}

        {/* Actions */}
        <Flex gap={2}>
          <Button variant="outline" size="sm" flex={1} onClick={onCancel} borderRadius={6}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            flex={1}
            onClick={handleSign}
            isDisabled={!password.trim()}
            borderRadius={6}
          >
            Sign
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
};

export default SigningConfirmationContent;
